import { useMutation, useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { AlertTriangle, ArrowRight, Check, Minus, Plus, RefreshCw } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";

import { ApiError, api } from "../../api/client";
import type {
  Attrition,
  Order,
  RotationResults,
  RotationSummary,
  WingoApplied,
  WingoCell,
} from "../../api/types";
import { Checkbox } from "../../components/Checkbox";
import { Dialog, DialogBody } from "../../components/Dialog";
import { EmptyState } from "../../components/EmptyState";
import { Select } from "../../components/Select";
import { Badge, Button, IconButton, inputClass, SectionLabel, textareaClass } from "../../components/ui";
import { k, t, tServer } from "../../i18n/i18n";
import { term } from "../../i18n/vocabulary";
import { plural } from "../../plural";
import { useUi } from "../../state/ui";
import { useUnsavedDraft } from "../../state/unsavedDrafts";
import { Waiting } from "../../components/Waiting";

/** Rotation CENTAC, reprises de l'iPad : éléments de la saisie des résultats d'un OPO (`ResultsView`), fenêtre
 *  du WINGO (le compléter avant ou après sa génération, registre A120), réécriture d'un OPO depuis le WINGO. */

export function Block({ title, children, hint }: { title: string; children: ReactNode; hint?: string }) {
  return (
    <section className="space-y-2 border-b border-line px-4 py-3 last:border-b-0">
      <h3 className="text-ui leading-snug font-semibold text-ink">{title}</h3>
      {hint && <p className="text-meta leading-snug text-ink-muted italic">{hint}</p>}
      {children}
    </section>
  );
}

/** Rubrique d'un bloc, en petites capitales (`SectionLabel`). */
function Sub({ children }: { children: ReactNode }) {
  return (
    <SectionLabel inset className="pt-1">
      {children}
    </SectionLabel>
  );
}

export function TextArea({
  value,
  onChange,
  placeholder,
  rows = 3,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <textarea className={textareaClass} rows={rows} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
  );
}

function capopsLabel(value: number): string {
  return value > 100 ? t("Renforcé (+{n} %)", { n: value - 100 }) : t("{n} %", { n: value });
}

function capopsTone(value: number): string {
  return value > 100 ? "text-accent font-semibold" : value < 50 ? "text-danger" : "text-ink-soft";
}

export function average(r: Pick<RotationResults, "edcCAPOPS" | "eeiCAPOPS">): number {
  return Math.floor((r.edcCAPOPS + r.eeiCAPOPS) / 2);
}

export function errorText(err: unknown): string {
  return err instanceof ApiError ? err.message : t("Opération impossible");
}

// --- Saisie des résultats -------------------------------------------------------------------------------

export type Entry = Pick<
  RotationResults,
  | "edcCAPOPS"
  | "eeiCAPOPS"
  | "edcUnitType"
  | "eeiUnitType"
  | "edcAttrition"
  | "eeiAttrition"
  | "terrainStatus"
  | "missionStatus"
  | "animatorNotes"
>;

const NO_ATTRITION: Attrition = { officiers: 0, sousOfficiers: 0, evat: 0, materielsMajeurs: {} };

/** Nom affiché d'un matériel majeur ; le nom enregistré (repris de l'iPad) ne change pas. */
const MATERIEL_LABELS: Record<string, string> = {
  AMX10RC: "AMX-10 RC",
  AMX10P: "AMX-10 P",
};
/** Matériels désignés par un nom commun, traduit à l'affichage (les autres sont des noms propres). */
const MATERIEL_NAMES: Record<string, string> = {
  "Mortier 81mm": k("Mortier 81 mm"),
  "Mortier 120mm": k("Mortier 120 mm"),
  "VAB sanitaire": k("VAB sanitaire"),
};
const materielLabel = (name: string) => {
  const common = MATERIEL_NAMES[name];
  return common ? t(common) : (MATERIEL_LABELS[name] ?? name);
};

function clamp(value: number, max: number): number {
  return Math.min(max, Math.max(0, value));
}

/** Compteur − / + : chaque pas part de l'état courant (deux clics rapides comptent deux fois). */
function Stepper({ label, value, max, onStep }: { label: string; value: number; max: number; onStep: (delta: number) => void }) {
  return (
    <div className="flex min-h-7 items-center justify-between gap-2">
      <span className="text-ui text-ink">{label}</span>
      <span className="flex items-center gap-1">
        <span className={clsx("w-8 text-right text-ui font-semibold tabular-nums", value > 0 ? "text-warn" : "text-ink-muted")}>{value}</span>
        <IconButton size="xs" variant="outline" disabled={value <= 0} onClick={() => onStep(-1)} aria-label={t("{label} : retirer", { label })}>
          <Minus size={12} />
        </IconButton>
        <IconButton size="xs" variant="outline" disabled={value >= max} onClick={() => onStep(1)} aria-label={t("{label} : ajouter", { label })}>
          <Plus size={12} />
        </IconButton>
      </span>
    </div>
  );
}

export function CapopsRow({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-ui text-ink">{label}</span>
        <span className={clsx("text-ui tabular-nums", capopsTone(value))}>{capopsLabel(value)}</span>
      </div>
      <input
        type="range"
        min={0}
        max={130}
        step={5}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-1 w-full cursor-pointer accent-accent"
        aria-label={t("CAPOPS {unit}", { unit: label })}
      />
    </div>
  );
}

export function AttritionBlock({
  label,
  unit,
  entry,
  setEntry,
  summary,
}: {
  label: string;
  unit: "edc" | "eei";
  entry: Entry;
  setEntry: (update: (e: Entry) => Entry) => void;
  summary: RotationSummary;
}) {
  const typeKey = unit === "edc" ? "edcUnitType" : "eeiUnitType";
  const attritionKey = unit === "edc" ? "edcAttrition" : "eeiAttrition";
  const type = entry[typeKey] ?? summary.displayed_unit_type[unit];
  const attrition = entry[attritionKey] ?? NO_ATTRITION;
  const setAttrition = (update: (a: Attrition) => Attrition) =>
    setEntry((e) => ({ ...e, [attritionKey]: update(e[attritionKey] ?? NO_ATTRITION) }));
  return (
    <Block title={t("Attrition {unit}", { unit: label })}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-meta whitespace-nowrap text-ink-soft">{t("Type d'unité")}</span>
        <span className="w-56 shrink-0">
        <Select
          className={inputClass}
          value={type}
          onChange={(e) => {
            const next = e.target.value;
            setEntry((cur) => ({
              ...cur,
              [typeKey]: next,
              // Changer de type vide les matériels saisis (catalogue différent), comme l'iPad.
              [attritionKey]: next !== (cur[typeKey] ?? summary.displayed_unit_type[unit])
                ? { ...(cur[attritionKey] ?? NO_ATTRITION), materielsMajeurs: {} }
                : cur[attritionKey],
            }));
          }}
        >
          {Object.keys(summary.unit_types).map((name) => (
            <option key={name} value={name}>
              {term(name)}
            </option>
          ))}
        </Select>
        </span>
      </div>
      <Sub>{t("Effectif (HC)")}</Sub>
      <Stepper label={t("Officiers")} value={attrition.officiers} max={30} onStep={(d) => setAttrition((a) => ({ ...a, officiers: clamp(a.officiers + d, 30) }))} />
      <Stepper label={t("Sous-officiers")} value={attrition.sousOfficiers} max={60} onStep={(d) => setAttrition((a) => ({ ...a, sousOfficiers: clamp(a.sousOfficiers + d, 60) }))} />
      <Stepper label={t("EVAT")} value={attrition.evat} max={200} onStep={(d) => setAttrition((a) => ({ ...a, evat: clamp(a.evat + d, 200) }))} />
      <Sub>{t("Matériels majeurs (HC)")}</Sub>
      {(summary.unit_types[type] ?? []).map((materiel) => (
        <Stepper
          key={materiel}
          label={materielLabel(materiel)}
          value={attrition.materielsMajeurs[materiel] ?? 0}
          max={20}
          onStep={(d) =>
            setAttrition((a) => {
              const materiels = { ...a.materielsMajeurs };
              const v = clamp((materiels[materiel] ?? 0) + d, 20);
              if (v === 0) delete materiels[materiel];
              else materiels[materiel] = v;
              return { ...a, materielsMajeurs: materiels };
            })
          }
        />
      ))}
    </Block>
  );
}

// --- WINGO ----------------------------------------------------------------------------------------------

type WingoFields = Pick<
  RotationResults,
  | "eniSituationUpdate"
  | "eniResidualVolume"
  | "eniResidualDirection"
  | "killBoxActive"
  | "killBoxActivationGDH"
  | "killBoxDeactivationAuthority"
  | "fragoLogText"
  | "wingoMissionGTIA"
  | "wingoIdeeManoeuvre"
  | "retainedME1Code"
  | "retainedME2Code"
>;

const WINGO_KEYS: (keyof WingoFields)[] = [
  "eniSituationUpdate",
  "eniResidualVolume",
  "eniResidualDirection",
  "killBoxActive",
  "killBoxActivationGDH",
  "killBoxDeactivationAuthority",
  "fragoLogText",
  "wingoMissionGTIA",
  "wingoIdeeManoeuvre",
  "retainedME1Code",
  "retainedME2Code",
];

type Contextual = Record<"EDC" | "EEI", Record<number, string>>;

function MissionCell({ cell, value, onChange }: { cell: WingoCell; value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1.5 rounded-lg border border-line p-2.5">
      <div className="flex flex-wrap items-center gap-1.5 text-ui">
        <span className="font-semibold text-ink tabular-nums">{cell.label} —</span>
        <span className={clsx("font-medium", cell.rule ? "text-warn" : "text-ink")}>{tServer(cell.doctrinal)}</span>
        {cell.coordinated && <span className="text-meta text-ink-muted">{t("(coord.)")}</span>}
        {cell.rule && <Badge tone="warn">WINGO</Badge>}
      </div>
      {cell.rule && (
        <p className="flex items-start gap-1.5 text-meta text-warn">
          <ArrowRight size={13} className="mt-0.5 shrink-0" />
          <span>
            {t("Bascule proposée : {mission}. {rationale}", {
              mission: tServer(cell.rule.doctrinal),
              rationale: tServer(cell.rule.rationale),
            })}
          </span>
        </p>
      )}
      <TextArea value={value} onChange={onChange} rows={2} placeholder={t("Texte doctrinal : ex CTRL.S BOX X, escorte TC2, INTERCEPTER vers D73…")} />
    </div>
  );
}

export function WingoDialog({
  exerciseId,
  number,
  generated = false,
  onClose,
  onApplied,
}: {
  exerciseId: string;
  number: number;
  /** WINGO déjà généré (document) : « Mettre à jour le WINGO », sinon « Générer le WINGO ». */
  generated?: boolean;
  onClose: () => void;
  onApplied: (result: WingoApplied, regenerated: boolean) => void;
}) {
  const previewQuery = useQuery({
    queryKey: ["wingo-preview", exerciseId, number],
    queryFn: () => api.wingoPreview(exerciseId, number),
    staleTime: 0,
  });
  const preview = previewQuery.data;
  const initial = useMemo(() => {
    if (!preview) return null;
    const fields = Object.fromEntries(WINGO_KEYS.map((k) => [k, preview.results[k]])) as WingoFields;
    const contextual: Contextual = { EDC: {}, EEI: {} };
    for (const cell of preview.cells) if (cell.contextual) contextual[cell.unit][cell.phase] = cell.contextual;
    return { fields, contextual };
  }, [preview]);
  const [fields, setFields] = useState<WingoFields | null>(null);
  const [contextual, setContextual] = useState<Contextual | null>(null);
  const [regenerate, setRegenerate] = useState(true);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const notify = useUi((st) => st.notify);
  const setError = (text: string) => notify({ text, tone: "error" });
  const current = fields ?? initial?.fields ?? null;
  const texts = contextual ?? initial?.contextual ?? { EDC: {}, EEI: {} };
  const set = <K extends keyof WingoFields>(key: K, value: WingoFields[K]) =>
    setFields((f) => ({ ...(f ?? (initial?.fields as WingoFields)), [key]: value }));
  const setText = (unit: "EDC" | "EEI", phase: number, value: string) =>
    setContextual((c) => {
      const base = c ?? initial?.contextual ?? { EDC: {}, EEI: {} };
      const perUnit = { ...base[unit] };
      if (value) perUnit[phase] = value;
      else delete perUnit[phase];
      return { ...base, [unit]: perUnit };
    });
  const changed =
    initial !== null && (JSON.stringify(current) !== JSON.stringify(initial.fields) || JSON.stringify(texts) !== JSON.stringify(initial.contextual));

  const apply = useMutation({
    mutationFn: () =>
      api.applyWingo(exerciseId, number, {
        results: current ?? {},
        contextual: {
          EDC: Object.fromEntries(Object.entries(texts.EDC).map(([k, v]) => [String(k), v])),
          EEI: Object.fromEntries(Object.entries(texts.EEI).map(([k, v]) => [String(k), v])),
        },
        regenerate,
      }),
    onSuccess: (result) => onApplied(result, regenerate),
    onError: (err) => setError(errorText(err)),
  });

  const close = () => (changed ? setConfirmDiscard(true) : onClose());
  useUnsavedDraft(`centac:wingo:${exerciseId}:${number}`, changed, { label: t("WINGO de l'OPO N°{n}", { n: number + 1 }), discard: onClose });
  const next = number + 1;
  const title = preview ? t("WINGO N°{n} du GTIA", { n: preview.wingo_number }) : "WINGO";

  if (!preview || !current) {
    return (
      <Dialog title={title} subtitle={t("Préparation de l'OPO N°{n}", { n: next })} onClose={onClose} width={720}>
        {previewQuery.isError ? (
          <EmptyState variant="error" compact>
            {errorText(previewQuery.error)}
          </EmptyState>
        ) : (
          <EmptyState variant="loading" compact title={t("Préparation du WINGO…")} />
        )}
      </Dialog>
    );
  }
  const r = preview.results;
  const ctxCount = Object.values(texts.EDC).filter(Boolean).length + Object.values(texts.EEI).filter(Boolean).length;
  const meName = (code: string | null) => {
    const name = preview.me_options.find((o) => o.key === code)?.name;
    return name && tServer(name);
  };

  return (
    <Dialog
      title={title}
      subtitle={t("Préparation de l'OPO N°{n}", { n: next })}
      onClose={close}
      width={720}
      footer={
        confirmDiscard ? (
          <>
            <span className="mr-auto flex items-center gap-1.5 text-meta text-warn">
              <AlertTriangle size={13} />{" "}
              {t("Les modifications saisies ne seront pas appliquées à l'OPO N°{n} et seront perdues.", { n: next })}
            </span>
            <Button variant="secondary" onClick={() => setConfirmDiscard(false)}>
              {t("Continuer la saisie")}
            </Button>
            <Button variant="danger" onClick={onClose}>
              {t("Abandonner")}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={close}>
              {t("Fermer")}
            </Button>
            <Button onClick={() => apply.mutate()} disabled={apply.isPending}>
              <Check size={14} />{" "}
              {apply.isPending ? (
                <Waiting>{t("Génération…")}</Waiting>
              ) : generated ? (
                t("Mettre à jour le WINGO N°{n}", { n: preview.wingo_number })
              ) : (
                t("Générer le WINGO N°{n}", { n: preview.wingo_number })
              )}
            </Button>
          </>
        )
      }
    >
      <div className="flex items-center justify-between border-b border-line px-4 py-2 text-meta text-ink-muted">
        <span className="font-mono">{preview.generation_gdh}</span>
        <span className="italic">{t("Référence — Carte 1/25000 coupure spéciale")}</span>
      </div>

      <Block title={t("Bilan OPO N°{n}", { n: number })}>
        <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-ui">
          <dt className="text-ink-soft">{t("CAPOPS EDC")}</dt>
          <dd className={clsx("text-right tabular-nums", capopsTone(r.edcCAPOPS))}>{capopsLabel(r.edcCAPOPS)}</dd>
          <dt className="text-ink-soft">{t("CAPOPS EEI")}</dt>
          <dd className={clsx("text-right tabular-nums", capopsTone(r.eeiCAPOPS))}>{capopsLabel(r.eeiCAPOPS)}</dd>
          <dt className="text-ink-soft">{t("CAPOPS moyenne GTIA")}</dt>
          <dd className="text-right font-semibold text-ink tabular-nums">{capopsLabel(preview.average_capops)}</dd>
          {(["edcAttrition", "eeiAttrition"] as const).map((key) => {
            const a = r[key];
            if (!a) return null;
            const people = a.officiers + a.sousOfficiers + a.evat;
            const mats = Object.entries(a.materielsMajeurs)
              .filter(([, v]) => v > 0)
              .map(([k, v]) => `${materielLabel(k)} (${v})`)
              .sort();
            if (!people && !mats.length) return null;
            return [
              <dt key={`${key}-t`} className="text-ink-soft">
                {t("Attrition {unit}", { unit: key === "edcAttrition" ? t("EDC") : t("EEI") })}
              </dt>,
              <dd key={`${key}-v`} className="text-right text-ink">
                {people > 0 && (
                  <span className="block">
                    {t("Effectif : {off} Off / {nco} SO / {evat} EVAT", { off: a.officiers, nco: a.sousOfficiers, evat: a.evat })}
                  </span>
                )}
                {mats.length > 0 && <span className="block">{t("Matériels : {list}", { list: mats.join(", ") })}</span>}
              </dd>,
            ];
          })}
          <dt className="text-ink-soft">{t("Statut mission")}</dt>
          <dd className="text-right text-ink">{term(r.missionStatus)}</dd>
          {r.terrainStatus && (
            <>
              <dt className="text-ink-soft">{t("Terrain tenu")}</dt>
              <dd className="text-right text-ink">{r.terrainStatus}</dd>
            </>
          )}
        </dl>
      </Block>

      <Block title={t("Appréciation du chef")}>
        <div className="flex items-center justify-between text-ui">
          <span className="text-ink-soft">{t("Posture")}</span>
          <span className="font-semibold text-ink">{term(preview.posture)}</span>
        </div>
        <p className="text-ui leading-relaxed text-ink">{tServer(preview.recommendation)}</p>
      </Block>

      <Block title={t("Situation ENI mise à jour")}>
        <Sub>{t("Reliquat ENI quantifié")}</Sub>
        <TextArea value={current.eniResidualVolume} onChange={(v) => set("eniResidualVolume", v)} rows={1} placeholder={t("ex : 1 section sur BMP3 + patrouille SRB renforcée T90")} />
        <Sub>{t("Direction probable")}</Sub>
        <TextArea value={current.eniResidualDirection} onChange={(v) => set("eniResidualDirection", v)} rows={1} placeholder={t("ex : exfiltration NORD-OUEST sur D73 vers SOMMESOUS")} />
        <Sub>{t("Mise à jour narrative (alimente §1.a de l'OPO N°{n})", { n: next })}</Sub>
        <TextArea
          value={current.eniSituationUpdate}
          onChange={(v) => set("eniSituationUpdate", v)}
          rows={3}
          placeholder={t("ex : Suite à la neutralisation de sa capacité d'embuscade anti-aérienne, l'ennemi aurait subi une désorganisation significative…")}
        />
      </Block>

      <Block title={t("§1.a iv — Modes d'action ENI (forçage manuel)")} hint={t("Par défaut, la rédaction choisit ME1/ME2 selon la posture doctrinale. Forcer un mode remplace ce choix.")}>
        {(["retainedME1Code", "retainedME2Code"] as const).map((key) => (
          <div key={key}>
            <Sub>{key === "retainedME1Code" ? t("ME1 (mode d'action probable)") : t("ME2 (mode d'action dangereux)")}</Sub>
            <Select className={inputClass} value={current[key] ?? ""} onChange={(e) => set(key, e.target.value || null)}>
              <option value="">{t("Auto (rédaction)")}</option>
              {preview.me_options.map((o) => (
                <option key={o.key} value={o.key}>
                  {tServer(o.name)}
                </option>
              ))}
            </Select>
          </div>
        ))}
      </Block>

      <Block title={t("§2 — Mission GTIA transitoire WINGO")} hint={t("Format normé : « En ECH1, à compter du <GDH>, [VERBE] la zone… jusqu'au <GDH+T1>, EMD [VERBE2] … ».")}>
        <TextArea
          value={current.wingoMissionGTIA}
          onChange={(v) => set("wingoMissionGTIA", v)}
          rows={3}
          placeholder={t("ex : En ECH1, à compter du 151401L JUN 26, CONTRÔLER la zone au NORD de la forêt de MAILLY face au NORD-OUEST jusqu'au 152000L JUN 26, EMD NEUTRALISER la 132(TI)BRD à compter du 160800L JUN 26.")}
        />
      </Block>

      <Block title={t("§3a — Idée de manœuvre WINGO")} hint={t("Format normé : « JE VEUX AVOIR + verbe + objet pour le <GDH> » + phasage T0/T1 narratif.")}>
        <TextArea
          value={current.wingoIdeeManoeuvre}
          onChange={(v) => set("wingoIdeeManoeuvre", v)}
          rows={3}
          placeholder={t("ex : En vue de préserver la liberté de manœuvre de la brigade et d'empêcher l'ennemi de se réarticuler, JE VEUX AVOIR NEUTRALISÉ l'ENI entre la D38 et la D98 pour le 161200L JUN 26… T0 : Dissimuler région fossé Aubert. T1 : INTERCEPTER et CONTRÔLER.")}
        />
      </Block>

      <Block
        title={t("Mission OPO N°{n} — phasage WINGO (T0/T1)", { n: next })}
        hint={t("Phasage WINGO doctrinal limité à T0/T1 (≤ 24h). Les phases T2+ et EMD seront couvertes par le prochain OPO ou un FRAGO.")}
      >
        {(["EDC", "EEI"] as const).map((unit) => (
          <div key={unit} className="space-y-1.5">
            <Sub>{unit === "EDC" ? t("EDC") : t("EEI")}</Sub>
            {preview.cells
              .filter((c) => c.unit === unit)
              .map((cell) => (
                <MissionCell key={`${unit}-${cell.phase}`} cell={cell} value={texts[unit][cell.phase] ?? ""} onChange={(v) => setText(unit, cell.phase, v)} />
              ))}
          </div>
        ))}
      </Block>

      <Block title={t("Coordination — Kill Box")}>
        <Checkbox checked={current.killBoxActive} onChange={(v) => set("killBoxActive", v)} label={t("Zone d'exclusion personnels débarqués active")} />
        {current.killBoxActive && (
          <>
            <Sub>{t("GDH d'activation")}</Sub>
            <input className={clsx(inputClass, "font-mono")} value={current.killBoxActivationGDH} onChange={(e) => set("killBoxActivationGDH", e.target.value)} placeholder={t("ex : {example}", { example: "152201L JUN 26" })} />
            <Sub>{t("Autorité de désactivation")}</Sub>
            <TextArea value={current.killBoxDeactivationAuthority} onChange={(v) => set("killBoxDeactivationAuthority", v)} rows={1} placeholder={t("ex : sur ordre chef d'équipe OAC de nuit")} />
          </>
        )}
      </Block>

      <Block title={t("FRAGO LOG (alimente §4 de l'OPO N°{n})", { n: next })}>
        <TextArea
          value={current.fragoLogText}
          onChange={(v) => set("fragoLogText", v)}
          rows={3}
          placeholder={t("ex : NLT 152200L Phase RAV IVO MONT CHICHET pour l'EEI, escorte et sécurisation a/c EEI. Axe LOG : D13.")}
        />
      </Block>

      {r.animatorNotes && (
        <Block title={t("Notes animateur")}>
          <p className="text-ui leading-relaxed text-ink">{r.animatorNotes}</p>
        </Block>
      )}

      {changed && (
        <Block title={t("Récapitulatif des modifications à appliquer")}>
          <ul className="space-y-1 text-ui">
            {[
              [t("Mise à jour ENI"), Boolean(current.eniSituationUpdate || current.eniResidualVolume || current.eniResidualDirection), null],
              [t("Mission GTIA transitoire"), Boolean(current.wingoMissionGTIA), null],
              [t("Idée de manœuvre"), Boolean(current.wingoIdeeManoeuvre), null],
              [t("ME1 forcé"), current.retainedME1Code !== null, meName(current.retainedME1Code)],
              [t("ME2 forcé"), current.retainedME2Code !== null, meName(current.retainedME2Code)],
              [t("Kill Box active"), current.killBoxActive, current.killBoxActivationGDH || null],
              [t("FRAGO LOG"), Boolean(current.fragoLogText), null],
              [t("Cellules EDC/EEI contextualisées"), ctxCount > 0, ctxCount > 0 ? plural(ctxCount, t("cellule"), t("cellules")) : null],
            ].map(([label, present, detail]) => (
              <li key={String(label)} className="flex items-center gap-2">
                <Check size={13} className={present ? "text-ok" : "text-line-strong"} />
                <span className={present ? "text-ink" : "text-ink-muted"}>{label}</span>
                {present && detail && <span className="ml-auto truncate text-meta text-ink-muted italic">{detail}</span>}
              </li>
            ))}
          </ul>
        </Block>
      )}

      <Block title={t("Régénération automatique de l'OPO N°{n}", { n: next })}>
        <Checkbox
          checked={regenerate}
          onChange={setRegenerate}
          label={t("Régénérer aussi les paragraphes narratifs")}
          detail={t("§1.a Situation ENI · §1.d Appréciation · §2 Mission · §3.a Manœuvre")}
        />
        <p className={clsx("flex items-start gap-1.5 text-meta italic", regenerate ? "text-warn" : "text-ink-muted")}>
          {regenerate ? <AlertTriangle size={12} className="mt-0.5 shrink-0" /> : <RefreshCw size={12} className="mt-0.5 shrink-0" />}
          {regenerate
            ? t("Les modifications manuelles de ces paragraphes dans l'OPO N°{n} seront remplacées.", { n: next })
            : t("Seuls la matrice §3.b et les blocs repris du WINGO (§1.a, §2, §3.a, §4) seront mis à jour.")}
        </p>
      </Block>
    </Dialog>
  );
}

// --- Réécriture d'un OPO --------------------------------------------------------------------------------

export function RegenerateDialog({
  exerciseId,
  number,
  onClose,
  onDone,
}: {
  exerciseId: string;
  number: number;
  onClose: () => void;
  onDone: (order: Order) => void;
}) {
  const notify = useUi((st) => st.notify);
  const setError = (text: string) => notify({ text, tone: "error" });
  const run = useMutation({
    mutationFn: () => api.regenerateFromWingo(exerciseId, number),
    onSuccess: onDone,
    onError: (err) => setError(errorText(err)),
  });
  const first = number === 1;
  const wingo = number * 10 + 1;
  return (
    <Dialog
      title={first ? t("Régénérer l'OPO N°1") : t("Mettre à jour l'OPO N°{n} depuis le WINGO N°{wingo}", { n: number, wingo })}
      subtitle={t("Réécriture des paragraphes narratifs (§1.a / §1.d / §2 / §3.a)")}
      onClose={onClose}
      width={520}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("Fermer")}
          </Button>
          <Button onClick={() => run.mutate()} disabled={run.isPending}>
            <RefreshCw size={14} /> {t("Réécrire les paragraphes")}
          </Button>
        </>
      }
    >
      <DialogBody className="text-ui">
        <p className="text-ink">
          {first
            ? t("Cette action régénère tous les paragraphes narratifs CENTAC selon la doctrine de la rotation.")
            : t("Cette action régénère les paragraphes narratifs de l'OPO N°{n} en intégrant le delta du WINGO de l'OPO précédent.", { n: number })}
        </p>
        <div>
          <div className="mb-1.5 font-semibold text-ink">{t("Paragraphes réécrits")}</div>
          <ul className="space-y-1.5">
            {[
              [t("§1.a SITUATION ENI"), t("Global / Initial / Ultérieur / Futur")],
              [t("§1.d APPRÉCIATION DU CHEF"), t("Posture AMI + réévaluation intention")],
              ["§2 MISSION", t("Verbe + objet + GDH + EMD")],
              [t("§3.a IDÉE DE MANŒUVRE"), t("Phasage T0/T1/T2/T3 narratif")],
            ].map(([label, detail]) => (
              <li key={label}>
                <span className="block font-medium text-ink">{label}</span>
                <span className="block text-meta text-ink-muted italic">{detail}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="flex items-start gap-1.5 text-meta text-warn">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          {t("Les modifications manuelles que vous avez faites sur ces paragraphes seront remplacées par la régénération.")}
        </p>
      </DialogBody>
    </Dialog>
  );
}
